import { Component, ErrorInfo, ReactNode } from 'react'
import {
  Typography,
  Button,
  Alert,
  Stack,
  Paper,
} from '@mui/material'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import RefreshIcon from '@mui/icons-material/Refresh'

interface Props {
  children: ReactNode
  fallback?: ReactNode
  onError?: (error: Error, errorInfo: ErrorInfo) => void
}

interface State {
  hasError: boolean
  error?: Error
}

class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
  }

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error }
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo)
    this.props.onError?.(error, errorInfo)
  }

  private handleRetry = () => {
    this.setState({ hasError: false, error: undefined })
  }

  public render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback
      }

      return (
        <Paper sx={{ p: 4, m: 2, textAlign: 'center' }}>
          <Stack spacing={3} alignItems="center" maxWidth={500} mx="auto">
            <ErrorOutlineIcon sx={{ fontSize: 64, color: 'error.main' }} />
            
            <Stack spacing={1} alignItems="center">
              <Typography variant="h5" component="h2" color="error.main" fontWeight={600}>
                Algo salió mal
              </Typography>
              
              <Typography variant="body1" color="text.secondary">
                Se produjo un error inesperado. Intenta recargar la página o contacta al soporte técnico.
              </Typography>
            </Stack>

            {typeof process !== 'undefined' && process.env?.NODE_ENV === 'development' && this.state.error && (
              <Alert severity="error" sx={{ width: '100%', textAlign: 'left' }}>
                <Typography variant="caption" component="pre" sx={{ whiteSpace: 'pre-wrap' }}>
                  {this.state.error.message}
                  {'\n\n'}
                  {this.state.error.stack}
                </Typography>
              </Alert>
            )}

            <Stack direction="row" spacing={2}>
              <Button
                variant="contained"
                startIcon={<RefreshIcon />}
                onClick={this.handleRetry}
              >
                Reintentar
              </Button>
              
              <Button
                variant="outlined"
                onClick={() => window.location.reload()}
              >
                Recargar página
              </Button>
            </Stack>
          </Stack>
        </Paper>
      )
    }

    return this.props.children
  }
}

export default ErrorBoundary